// Innovation Audit #9 — page snapshot publishing for the chat copilot.
//
// Each tab that hosts the chat drawer can publish a compact JSON
// snapshot of what the user is currently looking at. ChatDrawer reads
// this on every message and tucks it into pageContext.snapshot so the
// model can answer from on-screen data without calling read_* tools.
//
// Usage in a tab:
//   const setSnapshot = useSetChatSnapshot();
//   useEffect(() => {
//     setSnapshot({ kind: 'bom_view', payload: { rows: visibleRows.slice(0, 50) } });
//     return () => setSnapshot(null);
//   }, [visibleRows]);
//
// Or wrap a subtree:
//   <ChatSnapshotProvider value={{ kind: 'bom_view', payload: {...} }}>
//     ...
//   </ChatSnapshotProvider>

import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';

export interface ChatSnapshot {
  kind: 'bom_view' | 'specs_view' | 'flags_view' | 'opportunity_view' | 'sheets_view';
  payload: unknown;
}

const ChatSnapshotContext = createContext<ChatSnapshot | null>(null);
const ChatSnapshotSetterContext = createContext<(s: ChatSnapshot | null) => void>(() => {});

/**
 * Top-level provider — mount once near the root (e.g. in App.tsx). Holds the
 * snapshot in state and exposes the setter via useSetChatSnapshot().
 */
export function ChatSnapshotRoot({ children }: { children: ReactNode }) {
  const [snapshot, setSnapshot] = useState<ChatSnapshot | null>(null);
  // Stabilize the setter reference so child effects don't loop.
  const setter = useCallback((s: ChatSnapshot | null) => setSnapshot(s), []);
  return (
    <ChatSnapshotContext.Provider value={snapshot}>
      <ChatSnapshotSetterContext.Provider value={setter}>
        {children}
      </ChatSnapshotSetterContext.Provider>
    </ChatSnapshotContext.Provider>
  );
}

/** Read the currently published snapshot (used by ChatDrawer). */
export function useChatSnapshot(): ChatSnapshot | null {
  return useContext(ChatSnapshotContext);
}

/** Return the setter so a tab can publish + clear its snapshot. */
export function useSetChatSnapshot(): (s: ChatSnapshot | null) => void {
  return useContext(ChatSnapshotSetterContext);
}

/**
 * Convenience hook: publish a snapshot tied to the lifetime of the
 * calling component. Auto-clears on unmount. Pass `null` to clear.
 *
 * Uses a deep-equality check by reference so callers can pass a stable
 * memoized payload to avoid churn (and a JSON-string fast path otherwise).
 */
export function usePublishChatSnapshot(snapshot: ChatSnapshot | null): void {
  const setSnapshot = useSetChatSnapshot();
  // JSON-stringify guard so we don't re-publish identical payloads.
  const serialized = useMemo(() => (snapshot ? JSON.stringify(snapshot) : ''), [snapshot]);
  const lastSent = useRef<string>('');
  useEffect(() => {
    if (serialized === lastSent.current) return;
    lastSent.current = serialized;
    setSnapshot(snapshot);
    return () => {
      // Only clear if THIS component's published value is still active.
      if (lastSent.current === serialized) {
        setSnapshot(null);
        lastSent.current = '';
      }
    };
  }, [serialized, snapshot, setSnapshot]);
}
